import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { createPortal } from 'react-dom';
import { Menu, X, Trophy, Bookmark, FileText, Calendar, ChevronDown } from 'lucide-react';
import TestingValaLogo from './TestingValaLogo';
import ResumeBuilderRouter from './ResumeBuilderRouter';
import ContestSubmissionForm from './ContestSubmissionForm';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showResumeBuilder, setShowResumeBuilder] = useState(false);
  const [showContestForm, setShowContestForm] = useState(false);
  const [resumeToEdit, setResumeToEdit] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const { user, isVerified, showLogin } = useAuth();

  // Handle scroll for smart header behavior
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768 && isMenuOpen) {
        setIsMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };



  const openContestForm = () => {
    if (!user || !isVerified) {
      showLogin('contest', () => setShowContestForm(true));
      return;
    }
    setShowContestForm(true);
  };

  useEffect(() => {
    const handleNavigateToQA = () => {
      setShowResumeBuilder(true);
      window.__pendingNavigateToQA = false;
    };
    window.addEventListener('navigate-to-qa-builder', handleNavigateToQA);
    if (window.__pendingNavigateToQA) {
      handleNavigateToQA();
    }
    return () => window.removeEventListener('navigate-to-qa-builder', handleNavigateToQA);
  }, []);

  // Navigate to home section with smooth scroll
  const navigateToSection = (sectionId) => {
    // Fire event first so ResumeBuilderRouter closes before state update
    window.dispatchEvent(new CustomEvent('close-resume-builder'));
    flushSync(() => {
      setShowResumeBuilder(false);
    });
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', `/#${sectionId}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      setTimeout(() => {
        const el = document.getElementById(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 150);
    } else {
      window.history.pushState({}, '', `/#${sectionId}`);
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };


  const handleKeyActivate = (e, fn) => {
    if (!fn) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn(e);
    }
  };

  const headerContent = (
    <>
      <header
        data-debug-header="true"
        className={`bg-black/95 backdrop-blur-sm border-b border-gray-800 fixed top-0 left-0 w-full shadow-lg pointer-events-auto transition-all duration-300 header-safe-area ${isScrolled ? 'header-scrolled' : ''
          }`}
        style={{ borderWidth: '1px', zIndex: 9000 }}
      >
        <div className="relative">
          {/* Logo - Responsive positioning */}
          <div className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 z-50">
            <a
              href="/"
              onClick={(e) => {
                e.preventDefault();
                closeMenu();
                flushSync(() => {
                  setShowResumeBuilder(false);
                });
                window.history.pushState({}, '', '/');
                window.dispatchEvent(new PopStateEvent('popstate'));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              aria-label="Go to homepage"
              className="flex items-center bg-white px-2 sm:px-3 py-1 rounded-md shadow-md logo-container"
            >
              <TestingValaLogo
                iconSize={36}
                textSize={16}
                responsive={true}
              />
            </a>
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pl-32 sm:pl-36 md:pl-40">
            <div className="flex justify-end items-center h-14 sm:h-16">
              {/* Desktop Navigation - Hidden on mobile */}
              <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
                <a href="/" onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('close-resume-builder'));
                  flushSync(() => {
                    setShowResumeBuilder(false);
                  });
                  window.history.pushState({}, '', '/');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }} className="text-gray-300 hover:text-white font-medium transition-colors duration-200 text-sm lg:text-base flex items-center gap-1.5">
                  Home
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 hover:rotate-180" />
                </a>
                <a href="/#contest" onClick={(e) => { e.preventDefault(); navigateToSection('contest'); }} className="text-gray-300 hover:text-white font-medium transition-colors duration-200 text-sm lg:text-base flex items-center gap-1.5">
                  Contest
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 hover:rotate-180" />
                </a>
                <a href="/#community" onClick={(e) => { e.preventDefault(); navigateToSection('community'); }} className="text-gray-300 hover:text-white font-medium transition-colors duration-200 text-sm lg:text-base flex items-center gap-1.5">
                  Community
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 hover:rotate-180" />
                </a>
                <a href="/events" onClick={(e) => {
                  e.preventDefault();
                  closeMenu();
                  window.history.pushState({}, '', '/events');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }} className="text-gray-300 hover:text-white font-medium transition-colors duration-200 text-sm lg:text-base flex items-center gap-1.5">
                  Webinars
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 hover:rotate-180" />
                </a>
                <a href="/#about" onClick={(e) => { e.preventDefault(); navigateToSection('about'); }} className="text-gray-300 hover:text-white font-medium transition-colors duration-200 text-sm lg:text-base flex items-center gap-1.5">
                  About
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 hover:rotate-180" />
                </a>
                <a href="/#contact" onClick={(e) => { e.preventDefault(); navigateToSection('contact'); }} className="text-gray-300 hover:text-white font-medium transition-colors duration-200 text-sm lg:text-base flex items-center gap-1.5">
                  Contact
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 hover:rotate-180" />
                </a>

                <a
                  href="/my-board"
                  onClick={(e) => {
                    e.preventDefault();
                    window.dispatchEvent(new CustomEvent('close-resume-builder'));
                    flushSync(() => {
                      setShowResumeBuilder(false);
                    });
                    window.history.pushState({}, '', '/my-board');
                    window.dispatchEvent(new PopStateEvent('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="text-gray-300 hover:text-white font-medium transition-colors duration-200 px-2 lg:px-3 py-2 rounded-lg hover:bg-gray-800 text-sm lg:text-base flex items-center gap-1.5"
                >
                  My Board
                  <ChevronDown className="w-4 h-4 transition-transform duration-200 hover:rotate-180" />
                </a>

                <button onClick={() => {
                  setShowResumeBuilder(false);
                  openContestForm();
                }} className="ml-8 bg-blue-600 text-white px-4 lg:px-6 py-2 rounded-lg font-medium hover:bg-blue-700 hover:shadow-lg transition-all duration-200 flex items-center gap-2 border border-blue-500 text-sm lg:text-base">
                  <Trophy className="w-4 h-4" />
                  <span className="hidden lg:inline">Join Contest</span>
                  <span className="lg:hidden">Contest</span>
                </button>
              </nav>

              {/* Mobile menu button - Enhanced with better touch target */}
              <button
                className="md:hidden p-3 rounded-lg text-gray-300 hover:text-white hover:bg-gray-800 mobile-button mobile-touch-target no-select"
                onClick={toggleMenu}
                aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMenuOpen}
              >
                <div className="relative w-6 h-6">
                  <span className={`absolute top-1 left-0 w-6 h-0.5 bg-current hamburger-line ${isMenuOpen ? 'rotate-45 top-2.5' : ''}`} />
                  <span className={`absolute top-2.5 left-0 w-6 h-0.5 bg-current hamburger-line ${isMenuOpen ? 'opacity-0' : ''}`} />
                  <span className={`absolute top-4 left-0 w-6 h-0.5 bg-current hamburger-line ${isMenuOpen ? '-rotate-45 top-2.5' : ''}`} />
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Overlay */}
      <div className={`md:hidden fixed inset-0 z-[8999] transition-all duration-300 ${isMenuOpen ? 'visible' : 'invisible pointer-events-none'}`}>
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
          onClick={closeMenu}
        />

        {/* Mobile Menu Panel — offset accounts for header + safe-area-inset-top */}
        <div
          className={`absolute left-0 right-0 bg-black/95 mobile-menu-backdrop border-b border-gray-800 shadow-2xl mobile-menu-panel ${isMenuOpen ? 'open translate-y-0 opacity-100' : '-translate-y-full opacity-0'}`}
          style={{ top: 'calc(var(--header-h, 3.5rem) + max(0px, env(safe-area-inset-top)))' }}
        >
          <nav className="flex flex-col py-6 px-4 space-y-1">
            {/* Mobile Navigation Links */}
            {[
              { href: '/', label: 'Home', icon: null, section: null },
              { href: '/#contest', label: 'Contest', icon: null, section: 'contest' },
              { href: '/#community', label: 'Community', icon: null, section: 'community' },
              { href: '/events', label: 'Webinars', icon: null, section: null },
              { href: '/#about', label: 'About', icon: null, section: 'about' },
              { href: '/#contact', label: 'Contact', icon: null, section: 'contact' }
            ].map(({ href, label, icon: Icon, section }) => (
              <a
                key={href}
                href={href}
                onClick={(e) => {
                  e.preventDefault();
                  closeMenu();
                  if (section) {
                    navigateToSection(section);
                  } else {
                    window.dispatchEvent(new CustomEvent('close-resume-builder'));
                    flushSync(() => {
                      setShowResumeBuilder(false);
                    });
                    window.history.pushState({}, '', href);
                    window.dispatchEvent(new PopStateEvent('popstate'));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }
                }}
                className="text-gray-300 hover:text-white hover:bg-gray-800/50 font-medium nav-link mobile-nav-item mobile-touch-target px-4 py-3 rounded-lg flex items-center gap-3 text-base"
              >
                {Icon && <Icon className="w-5 h-5" />}
                {label}
              </a>
            ))}

            {/* Action Buttons */}
            <button
              onClick={() => {
                closeMenu();
                if (!user || !isVerified) {
                  showLogin('resume', () => setShowResumeBuilder(true));
                } else {
                  setShowResumeBuilder(true);
                }
              }}
              className="text-gray-300 hover:text-white hover:bg-gray-800/50 font-medium nav-link mobile-nav-item mobile-touch-target px-4 py-3 rounded-lg flex items-center gap-3 text-base w-full text-left"
            >
              AI Resume Builder
            </button>



            <a
              href="/my-board"
              onClick={(e) => {
                e.preventDefault();
                closeMenu();
                window.dispatchEvent(new CustomEvent('close-resume-builder'));
                flushSync(() => {
                  setShowResumeBuilder(false);
                });
                window.history.pushState({}, '', '/my-board');
                window.dispatchEvent(new PopStateEvent('popstate'));
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-gray-300 hover:text-white hover:bg-gray-800/50 font-medium nav-link mobile-nav-item mobile-touch-target px-4 py-3 rounded-lg flex items-center gap-3 text-base"
            >
              My Board
            </a>

            {/* Primary CTA */}
            <button
              onClick={() => {
                closeMenu();
                openContestForm();
              }}
              className="bg-blue-600 text-white px-6 py-4 rounded-lg font-medium hover:bg-blue-700 hover:shadow-lg mobile-button mobile-nav-item mobile-touch-target flex items-center justify-center gap-3 border border-blue-500 mt-4 text-base"
            >
              Join Contest
            </button>
          </nav>
        </div>
      </div>
    </>
  );

  // Render header into document.body to stay above other stacking contexts
  if (typeof document !== 'undefined') {
    return (
      <>
        {createPortal(headerContent, document.body)}
        {showResumeBuilder && (
          <ResumeBuilderRouter
            isOpen={showResumeBuilder}
            onClose={() => {
              setShowResumeBuilder(false);
              setResumeToEdit(null);
            }}
          />
        )}
        <ContestSubmissionForm
          isOpen={showContestForm}
          onClose={() => setShowContestForm(false)}
          contestData={{
            theme: 'Advanced Testing Methodologies',
            deadline: (() => {
              const now = new Date();
              const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
              const y = lastDay.getFullYear();
              const m = String(lastDay.getMonth() + 1).padStart(2, '0');
              const d = String(lastDay.getDate()).padStart(2, '0');
              return `${y}-${m}-${d}`;
            })()
          }}
        />
      </>
    );
  }

  return (
    <>
      {headerContent}

    </>
  );
};

export default Header;
